import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import HeroSection from 'components/sections/heroSection.js/heroSection';
import AboutUsSection from 'components/sections/aboutUs/aboutUs';
import { getOrderedSections } from 'controls/utils';
import FeaturedSection from 'components/sections/featured/featured';
import StoreBanner from 'components/sections/store/storeBanner';
import PromoCards from 'components/sections/promoCards/promoCards';
import QuickTips from 'components/sections/quickTips/quickTips';
import News from 'components/sections/news/news';
import Newsletter from 'components/sections/newsletter/newsletter';
import { useGetHomePageQuery } from 'app/api/gatewayApiSlice';
import MetaDecorator from 'components/layout/MetaDecorator';
import LoaderSpinner from 'components/atoms/Loader/LoaderSpinner';

const SCROLL_POSITION_KEY = 'home-scroll-position';

export function HomePage() {
  const { userInfo } = useSelector((state) => state.user);
  const hasRestoredScroll = useRef(false);

  const {
    data: homeData,
    isLoading: loadingHome,
  } = useGetHomePageQuery();

  const orderedSections = getOrderedSections(homeData?.sections || {});

  // Save scroll position
  useEffect(() => {
    const saveScrollPosition = () => {
      sessionStorage.setItem(
        SCROLL_POSITION_KEY,
        String(window.scrollY || document.documentElement.scrollTop),
      );
    };

    window.addEventListener('scroll', saveScrollPosition);

    return () => {
      saveScrollPosition();
      window.removeEventListener('scroll', saveScrollPosition);
    };
  }, []);

  useEffect(() => {
    if (
      hasRestoredScroll.current
    || loadingHome
    || !userInfo
    || orderedSections.length === 0
    ) {
      return;
    }

    const savedPosition = sessionStorage.getItem(SCROLL_POSITION_KEY);

    if (savedPosition) {
    // Wait until the DOM is fully loaded
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.scrollTo(0, Number(savedPosition));
          window.dispatchEvent(new Event('scroll'));
        });
      });
    }

    hasRestoredScroll.current = true;
  }, [loadingHome, userInfo, orderedSections.length]);

  if (loadingHome) {
    return <LoaderSpinner />;
  }

  return (
    <>
      <MetaDecorator
        title='Home - Roland Cloud'
        description='Discover Roland Cloud — premium virtual instruments, software, and hardware from Roland, the world leader in electronic musical instruments.'
        link=''
      />
      {orderedSections.map((section, index) => {
        const isFirst = index === 0;
        const isLast = index === orderedSections.length - 1;
        const isSpacedSection = ['aboutUs', 'featured', 'promo', 'quickTips', 'news'].includes(section.key);

        const sectionClass = [
          'section',
          isFirst && isSpacedSection ? 'extra-top-margin' : '',
          isLast && isSpacedSection ? 'extra-bottom-margin' : '',
        ]
          .filter(Boolean)
          .join(' ');

        return (
          <div className={sectionClass} key={section.key}>
            {(() => {
              switch (section.key) {
                case 'hero':
                  return <HeroSection data={section} />;
                case 'aboutUs':
                  return <AboutUsSection data={section} />;
                case 'featured':
                  return <FeaturedSection data={section} />;
                case 'store':
                  return <StoreBanner data={section} />;
                case 'promo':
                  return <PromoCards data={section} />;
                case 'quickTips':
                  return <QuickTips data={section} />;
                case 'news':
                  return <News data={section} />;
                case 'newsLetter':
                  return <Newsletter data={section} />;
                default:
                  return null;
              }
            })()}
          </div>
        );
      })}
    </>
  );
}
